import { OFFLINE_SCHEMA_VERSION } from './types';

const DB_PREFIX = 'platzona-offline-';

export function dbNameForPlatform(platform: string): string {
  return `${DB_PREFIX}${platform}`;
}

export async function openOfflineDb(platform: string): Promise<IDBDatabase> {
  const name = dbNameForPlatform(platform);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(name, OFFLINE_SCHEMA_VERSION);
    req.onerror = () => reject(req.error || new Error('indexedDB.open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('outbox')) {
        const outbox = db.createObjectStore('outbox', { keyPath: 'id' });
        outbox.createIndex('byStatus', 'status', { unique: false });
        outbox.createIndex('byNextAttempt', 'nextAttemptAt', { unique: false });
      }
      if (!db.objectStoreNames.contains('kv')) {
        db.createObjectStore('kv', { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains('siteSnapshots')) {
        db.createObjectStore('siteSnapshots', { keyPath: 'slug' });
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta', { keyPath: 'key' });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      const tx = db.transaction('meta', 'readwrite');
      tx.objectStore('meta').put({ key: 'schemaVersion', value: OFFLINE_SCHEMA_VERSION });
      resolve(db);
    };
  });
}

/**
 * One-time migration for a platform rename (e.g. ToView -> PlatzView): if a
 * browser still has the OLD platform's IndexedDB with queued outbox/kv/snapshot
 * data sitting in it, copy every record into the new platform's database, then
 * remove the old one. Without this, any offline-queued mutation made before a
 * rename would be silently stranded — the app only ever opens the new name
 * going forward and never looks at the old one again.
 *
 * Safe no-op when: the browser doesn't support `indexedDB.databases()` (older
 * Firefox/Safari), the old database was never created, or it's already empty.
 * Never throws — a failed migration should degrade to "old data stays put and
 * unused", not break the app from loading.
 */
export async function migrateOfflineDbName(oldPlatform: string, newPlatform: string): Promise<void> {
  if (typeof indexedDB === 'undefined' || typeof indexedDB.databases !== 'function') return;
  try {
    const oldName = dbNameForPlatform(oldPlatform);
    const known = await indexedDB.databases();
    if (!known.some((d) => d.name === oldName)) return;

    const oldDb = await new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(oldName);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error || new Error('open old db failed'));
    });

    const migratableStores = ['outbox', 'kv', 'siteSnapshots'] as const;
    const toCopy: Partial<Record<(typeof migratableStores)[number], unknown[]>> = {};
    let hasData = false;
    for (const storeName of migratableStores) {
      if (!oldDb.objectStoreNames.contains(storeName)) continue;
      const records = await idbReq(oldDb.transaction(storeName, 'readonly').objectStore(storeName).getAll());
      if (records.length) { toCopy[storeName] = records; hasData = true; }
    }
    oldDb.close();

    if (!hasData) { await deleteOfflineDb(oldPlatform); return; }

    const newDb = await openOfflineDb(newPlatform);
    for (const storeName of migratableStores) {
      const records = toCopy[storeName];
      if (!records) continue;
      const tx = newDb.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      for (const record of records) store.put(record);
      await idbTxDone(tx);
    }
    newDb.close();
    await deleteOfflineDb(oldPlatform);
  } catch {
    // Best-effort — leave the old database in place rather than risk the app failing to load.
  }
}

export async function deleteOfflineDb(platform: string): Promise<void> {
  const name = dbNameForPlatform(platform);
  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error || new Error('deleteDatabase failed'));
    req.onblocked = () => resolve();
  });
}

export function idbReq<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IDB request failed'));
  });
}

export function idbTxDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error('IDB transaction failed'));
    tx.onabort = () => reject(tx.error || new Error('IDB transaction aborted'));
  });
}
